import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { PasswordContainer } from '../../../../components/PasswordContainer';
import { SettingsPageContainer } from '../../../../components/Settings/SettingsPageContainer';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useQuestion } from '../../../../hooks/question';
import { useMessage } from '../../../../hooks/message';
import { useRPC } from '../../../../hooks/rpc';
import { useKeyboard } from '../../../../hooks/keyboard';
import { SettingInputComponent } from '../../../../components/Settings/SettingInputComponent';
import { useSelector } from 'react-redux';
import {
    CONTROL_TYPE_ID_ALARM,
    CONTROL_TYPE_ID_SIREN,
    CONTROL_TYPE_ID_THERMOSTAT,
    INPUT_TYPE_ID_SCENARIO,
    INPUT_TYPE_ID_SENSOR,
    InputModel,
    InputTypeModel, SensorTypeModel, CONTROL_TYPE_ADV_THERMOSTAT, INPUT_TYPE_ID_ADV_TEMPERATURE, CONTROL_TYPE_GENERAL_TEMPERATURE, INPUT_TYPE_ID_KEYPAD, BRIDGE_DEVICE_TYPE_ID_KEYPAD,
    BRIDGE_DEVICE_TYPE_ID_KNX,
    INPUT_TYPE_ID_ANALOG,
} from '../../../../types/control';
import { SelectItem } from '../../../../components/Select';
import { BridgeDeviceModel } from '../../../../types/bridge-device';
import { SettingSelectComponent } from '../../../../components/Settings/SettingSelectComponent';
import { useSetSelector } from '../../../../hooks/set-selector';
import { SettingCheckBoxComponent } from '../../../../components/Settings/SettingCheckboxComponent';
import { useMap } from '../../../../hooks/map';
import { useFilterSelector } from '../../../../hooks/filter-selector';
import { useLocalization } from "../../../../hooks/localization";
import { CONTROL_TYPE_ID_FANCOIL } from '../../../../types/control';
import { SettingReadOnlyRowComponent } from '../../../../components/Settings/SettingReadOnlyRowComponent';
import { isValidKNXValue, isValidNumber } from '../../../../utils/validation';
import { AnalogSensorTypeModel } from '../../../../types/analog-sensor';
import { AnalogSensorParameters } from '../../../../types/control';
const BACK_URL = '/settings/input';

type CONTROL_VALUE = {
    id: string;
    isVirtual: boolean;
};

export const InputEditScreen = memo(function InputEditScreen() {
    const { t } = useLocalization();
    const params = useParams<{ id: string }>();
    const question = useQuestion();
    const { success, failure } = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();
    const location = useLocation<{ controlId: string; bridgeDeviceId: string; inputTypeId: string; input: InputModel; }>();
    const previousLocationSelectedControl = useSelector(state => state.control.controlList.find(c => c.id === location?.state?.controlId));
    const analogSensorTypeList = useSelector(state => state.control.analogSensorTypeList);
    const analogSensorTypeOptionList: SelectItem<AnalogSensorTypeModel>[] = useMemo(() => analogSensorTypeList.map(sensorType => ({
        value: sensorType,
        label: sensorType.name,
    })), [analogSensorTypeList]);
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [setActiveNumpadInput, renderNumpadKeyboard] = useKeyboard("numpad", true)

    const inputList = useSelector(state => state.control.inputList);
    const bridgeDeviceTypeSet = useSetSelector(state => state.control.bridgeDeviceTypeList);
    const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList);
    const bridgeDeviceOptionList: SelectItem<BridgeDeviceModel>[] =
        useMemo(() => bridgeDeviceList
            .filter(bridgeDevice => (bridgeDeviceTypeSet[bridgeDevice.type_id]?.input_count || 0) > 0)
            .sort((a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : 0)
            .map(bridgeDevice => ({
                value: bridgeDevice,
                label: bridgeDevice.name,
            })), [bridgeDeviceList, bridgeDeviceTypeSet]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selected = useMemo(() => location.state?.input || inputList.find(control => control.id === params?.id) || null, []);
    const [bridgeDevice, setBridgeDevice] = useState<SelectItem<BridgeDeviceModel>>(
        (selected == null ? (location?.state?.bridgeDeviceId == null ? null :
            bridgeDeviceOptionList.find(bridgeDeviceOption => bridgeDeviceOption.value.id === location?.state?.bridgeDeviceId) || null) :
            bridgeDeviceOptionList.find(bridgeDeviceOption => bridgeDeviceOption.value.id === selected.bridge_device_id)
            || null),
    );
    const bridgeDeviceType = useMemo(() => bridgeDevice == null ? null : bridgeDeviceTypeSet[bridgeDevice.value.type_id],
        [bridgeDevice, bridgeDeviceTypeSet]);
    const controlSet = useSetSelector(state => state.control.controlList);
    const inputTypeList = useSelector(state => state.control.inputTypeList);
    const inputTypeOptionList: SelectItem<InputTypeModel>[] =
        useMemo(() => inputTypeList.filter(filteredType => selected?.id == null ? filteredType.id !== '6' : filteredType)
            .filter(value => bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_KEYPAD ? (value.id === INPUT_TYPE_ID_KEYPAD || value.id === INPUT_TYPE_ID_SCENARIO) : value?.id !== INPUT_TYPE_ID_KEYPAD).filter(value => bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_KNX ? (value.id === INPUT_TYPE_ID_SCENARIO) : value)
            .map(inputType => ({
                value: inputType,
                label: inputType.name
            })), [inputTypeList, bridgeDeviceType, selected?.id]);
    const [inputType, setInputType] = useState<SelectItem<InputTypeModel>>(
        (selected == null ? (location?.state?.inputTypeId == null ? null :
            inputTypeOptionList.find(inputTypeOption => inputTypeOption.value.id === location.state.inputTypeId)
            || null) : inputTypeOptionList.find(inputTypeOption => inputTypeOption.value.id === selected.type_id)
        || null),
    );
    const [hysteresis, setHysteresis] = useState<number>(
        selected?.analog_sensor_settings?.hysteresis || 0
    );
    const [remoteInput, setRemoteInput] = useState(selected?.remote_input || false)
    // 0 setAlarm input type
    const sensorTypeOptionList = useMap(
        useFilterSelector(state => state.control.sensorTypeList, sensor => sensor.id !== '0', []),
        state => ({ value: state, label: state.name }),
        []);
    const [sensorTypeOption, setSensorTypeOption] = useState<SelectItem<SensorTypeModel>>(
        (selected == null || selected.type_id !== INPUT_TYPE_ID_SENSOR ? null : (
            sensorTypeOptionList.find(sensorTypeOption => sensorTypeOption.value.id === selected.sensor_settings?.sensor_type_id)
            || null)
        ),
    );
    const [analogSensorTypeOption, setAnalogSensorTypeOption] = useState<SelectItem<AnalogSensorTypeModel>>(
        (selected == null || selected.type_id !== INPUT_TYPE_ID_ANALOG ? null : (
            analogSensorTypeOptionList.find(
                analogSensorTypeOption =>
                    analogSensorTypeOption.value.id === selected.analog_sensor_settings?.analog_sensor_type_id
            ) || null)
        ),
    );
    const isInputTypeSensor = useMemo(() => inputType?.value?.id === INPUT_TYPE_ID_SENSOR || false, [inputType]);

    const bridgeDeviceFilteredAdvTempControlOptionList: SelectItem<CONTROL_VALUE>[] = useMap(
        useFilterSelector(
            state => state.control.controlList,
            control =>
                (
                    remoteInput ? control?.bridge_device_id !== bridgeDevice?.value?.id : control.bridge_device_id === bridgeDevice?.value.id
                ) && inputType?.value?.id === INPUT_TYPE_ID_ADV_TEMPERATURE && (
                    control.type_id === CONTROL_TYPE_ID_FANCOIL ||
                    control.type_id === CONTROL_TYPE_ADV_THERMOSTAT ||
                    control.type_id === CONTROL_TYPE_GENERAL_TEMPERATURE
                ),
            [bridgeDevice?.value, inputType?.value, remoteInput]
        ),
        controlModel => ({ label: controlModel.name, value: { isVirtual: false, id: controlModel.id } }),
        []
    )
    const bridgeDeviceFilteredControlOptionList: SelectItem<CONTROL_VALUE>[] = useMap(
        useFilterSelector(
            state => state.control.controlList,
            control => (remoteInput ? control?.bridge_device_id !== bridgeDevice?.value?.id : control.bridge_device_id === bridgeDevice?.value.id) && inputType?.value?.id !== '6' &&
                control.type_id !== CONTROL_TYPE_ID_THERMOSTAT /*&& control.type_id !== CONTROL_TYPE_ADV_THERMOSTAT*/ && (
                    isInputTypeSensor ?
                        control.type_id === CONTROL_TYPE_ID_ALARM :
                        (control.type_id !== CONTROL_TYPE_ID_ALARM && control.type_id !== CONTROL_TYPE_ID_SIREN)
                ),
            [isInputTypeSensor, bridgeDevice?.value, inputType?.value, remoteInput],
        ),
        controlModel => ({ label: controlModel.name, value: { isVirtual: false, id: controlModel.id } }),
        []);

    const bridgeDeviceFilteredVirtualControlOptionList: SelectItem<CONTROL_VALUE>[] = useMap(
        useFilterSelector(
            state => state.control.virtualControlList,
            virtualControl => (remoteInput ? virtualControl?.bridge_device_id !== bridgeDevice?.value?.id : virtualControl.bridge_device_id === bridgeDevice?.value.id) && (
                isInputTypeSensor ?
                    controlSet[virtualControl.control_id]?.type_id === CONTROL_TYPE_ID_ALARM :
                    (controlSet[virtualControl.control_id]?.type_id !== CONTROL_TYPE_ID_ALARM &&
                        controlSet[virtualControl.control_id]?.type_id !== CONTROL_TYPE_ID_SIREN)
            ),
            [isInputTypeSensor, bridgeDevice?.value, remoteInput],
        ),
        virtualControl => ({
            label: (controlSet[virtualControl.control_id]?.name || '') + ' (' + virtualControl.slot + ')',
            value: { isVirtual: true, id: virtualControl.id },
        }),
        [controlSet]);

    const bridgeDeviceFilteredAnalogSensorControlOptionList: SelectItem<CONTROL_VALUE>[] = useMap(useFilterSelector(
        state => state.control.controlList,
        control => (
            bridgeDevice?.value?.id === control.bridge_device_id &&
            (control?.parameters as AnalogSensorParameters)?.is_analog_sensor_control === true
        ),
        [bridgeDevice?.value?.id],
    ), control => ({
        value: { id: control.id, isVirtual: false },
        label: control.name,
    }));

    /*const bridgeDeviceFilteredVirtualControlOptionList: SelectItem<CONTROL_VALUE>[] = useMap(
        useFilterSelector(
            state => state.control.virtualControlList,
            virtualControl =>  (remoteInput ? virtualControl?.bridge_device_id !== bridgeDevice?.value?.id : virtualControl?.bridge_device_id === bridgeDevice?.value?.id) && (
                isInputTypeSensor ?
                    controlSet[virtualControl.control_id]?.type_id === CONTROL_TYPE_ID_ALARM :
                    (controlSet[virtualControl.control_id]?.type_id !== CONTROL_TYPE_ID_ALARM && controlSet[virtualControl.control_id]?.type_id !== CONTROL_TYPE_ID_SIREN) || (inputType?.value?.id === INPUT_TYPE_ID_ADV_TEMPERATURE && (controlSet[virtualControl?.control_id]?.type_id === CONTROL_TYPE_ID_FANCOIL || controlSet[virtualControl?.control_id]?.type_id === CONTROL_TYPE_ADV_THERMOSTAT))
            ),
            [isInputTypeSensor, bridgeDevice?.value, remoteInput],
        ),
        virtualControl => ({
            label: controlSet[virtualControl.control_id]?.name || '',
            value: {isVirtual: true, id: virtualControl.id},
        }),
        [controlSet]);*/
    const controlOptionList: SelectItem<CONTROL_VALUE>[] = useMemo(
        () => {
            if (inputType?.value?.id === INPUT_TYPE_ID_ANALOG) {
                return bridgeDeviceFilteredAnalogSensorControlOptionList
            }
            return [
                ...bridgeDeviceFilteredAdvTempControlOptionList,
                ...bridgeDeviceFilteredControlOptionList,
                ...bridgeDeviceFilteredVirtualControlOptionList
            ]
        },
        [
            inputType,
            bridgeDeviceFilteredControlOptionList,
            bridgeDeviceFilteredVirtualControlOptionList,
            bridgeDeviceFilteredAdvTempControlOptionList,
            bridgeDeviceFilteredAnalogSensorControlOptionList
        ]
    )
    const inputNumberOptionList: SelectItem<number>[] = useMemo(() => {
        const inputCount = bridgeDeviceType?.input_count || 0;
        if (inputCount === 0) {
            return [];
        }

        const list = [];
        for (let i = 0; i < inputCount; i++) {
            list.push({ value: i, label: t('input') + ' ' + i.toString() });
        }

        return list;
    }, [t, bridgeDeviceType]);

    const [name, setName] = useState((selected?.id != null && selected?.name) || '');
    const [knxSetAddress, setKnxSetAddress] = useState((selected?.id != null && selected?.knx_settings?.address) || "")
    const [pinCode, setPinCode] = useState((selected?.keypad_settings != null && selected?.keypad_settings?.pin) || "")
    const [controlOption, setControlOption] = useState<SelectItem<CONTROL_VALUE>>(
        selected == null ? null : (
            selected?.is_virtual_control
            ? bridgeDeviceFilteredVirtualControlOptionList.find(option => option?.value?.id === selected?.relational_id)
            : bridgeDeviceFilteredControlOptionList.find(option => option?.value?.id === selected?.relational_id) ||
            bridgeDeviceFilteredAdvTempControlOptionList.find(option => option?.value?.id === selected?.relational_id)
        ) || null
    )
    const [inputNumber, setInputNumber] = useState<SelectItem<number>>(
        (selected == null ? null : inputNumberOptionList.find(option => option?.value === selected?.input_number)
            || null),
    );
    const DIRECTION_OPTION_LIST = useMemo<SelectItem<number>[]>(() => [
        {
            value: 0,
            label: t('close_stop'),
        },
        {
            value: 1,
            label: t('open_up'),
        },
        {
            value: 2,
            label: t('down'),
        },
        {
            value: 3,
            label: t('open_close'),
        },
    ], [t]);
    const [direction, setDirection] = useState<SelectItem<number>>(
        (selected == null ? DIRECTION_OPTION_LIST[0] : DIRECTION_OPTION_LIST.find(option => option?.value === selected?.direction)
            || null),
    );
    const [outputReverse, setOutputReverse] = useState(selected?.should_output_reverse || false);

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const onDeletePressed = useCallback(() => {
        question(t('are_you_sure_to_delete_the_input'), () => {
            return rpc.deleteInput(selected?.id)
                .then(() => {
                    success(t('input_successfully_deleted'));
                    onBackPressed();
                })
                .catch(failure);
        });
    }, [
        t,
        failure,
        onBackPressed,
        question,
        rpc,
        selected?.id,
        success,
    ]);

    const isFieldsValid = useCallback((): boolean => {
        if (name == null || name.length === 0) {
            failure(t("pls_enter_name"))
            return false
        }

        if (bridgeDevice?.value == null) {
            failure(t("pls_select_device"))
            return false
        }

        if (inputType?.value == null) {
            failure(t("pls_select_a_input_type"))
            return false
        }
        if (inputType?.value?.id === INPUT_TYPE_ID_ANALOG) {
            if (analogSensorTypeOption?.value == null) {
                failure(t("pls_select_a_analog_sensor_type"))
                return false
            }
            if (hysteresis < 0 || hysteresis > 255) {
                failure(t('hysteresis_must_be_between_0_and_255'));
                return false;
            }
        }
        if (
            inputType?.value?.id !== INPUT_TYPE_ID_ANALOG &&
            inputType?.value?.id !== INPUT_TYPE_ID_ADV_TEMPERATURE &&
            bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KEYPAD &&
            bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KNX
        ) {
            if (inputNumber?.value == null) {
                failure(t("pls_select_a_input"))
                return false
            }
        }

        if (bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_KEYPAD) {
            if (pinCode.length !== 4 || !isValidNumber(pinCode)) {
                failure(t("pls_enter_a_valid_pin_code"))
                return false
            }
        }

        if (bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_KNX) {
            if (knxSetAddress === "" || knxSetAddress.length === 0) {
                failure(t("pls_enter_a_valid_value"))
                return false
            }
            if (!isValidKNXValue(knxSetAddress)) {
                failure(t("pls_enter_a_valid_value"))
                return false
            }
        }

        if (inputType.value.id !== INPUT_TYPE_ID_SCENARIO && inputType?.value?.id !== INPUT_TYPE_ID_ADV_TEMPERATURE) {
            if (direction?.value == null) {
                failure(t("pls_select_a_direction"))

                return false
            }

            if (controlOption?.value == null) {
                failure(t("pls_select_control"))
                return false
            }

            if (inputType.value.id === INPUT_TYPE_ID_SENSOR) {
                if (sensorTypeOption?.value == null) {
                    failure(t("pls_select_a_sensor_type"))
                    return false
                }
            }
        }

        return true
    }, [
        t,
        name,
        bridgeDevice?.value,
        inputType?.value,
        bridgeDeviceType?.id,
        failure,
        analogSensorTypeOption?.value,
        hysteresis,
        inputNumber?.value,
        pinCode,
        knxSetAddress,
        direction?.value,
        controlOption?.value,
        sensorTypeOption?.value
    ])
    const onGoBackPressed = useCallback(() => {
        if (previousLocationSelectedControl?.id != null) {
            success(t('control_successfully_saved'));
        }

        onBackPressed();
    }, [t, previousLocationSelectedControl?.id, success, onBackPressed]);

    const onCreatePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return
        }
        const determineInputNumber = () => {
            if (bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_KNX) {
                return 0;
            }
            if (inputType?.value?.id === INPUT_TYPE_ID_ANALOG) {
                return 0;
            }
            if (inputType?.value?.id === INPUT_TYPE_ID_ADV_TEMPERATURE && selected?.id != null) {
                return selected?.input_number;
            }
            return inputNumber?.value || null;
        }
        const input: InputModel = {
            id: selected?.id || null,
            is_active: true,
            is_notification: false,
            name,
            bridge_device_id: bridgeDevice.value.id,
            type_id: inputType.value.id,
            direction:
                inputType.value.id === INPUT_TYPE_ID_SCENARIO || inputType.value.id === INPUT_TYPE_ID_SENSOR
                    ? 1
                    : direction.value,
            is_virtual_control: controlOption?.value.isVirtual || false,
            relational_id: inputType.value.id === INPUT_TYPE_ID_SCENARIO ? null : controlOption?.value.id,
            should_output_reverse: outputReverse,
            input_number: determineInputNumber(),
            sensor_settings: inputType?.value?.id !== INPUT_TYPE_ID_SENSOR ? null : {
                sensor_type_id: sensorTypeOption?.value?.id,
                order: selected?.sensor_settings?.order
            },
            analog_sensor_settings: inputType?.value?.id !== INPUT_TYPE_ID_ANALOG ? null : {
                analog_sensor_type_id: analogSensorTypeOption?.value?.id,
                hysteresis: hysteresis
            },
            remote_input: remoteInput,
            keypad_settings:
                bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_KEYPAD
                    ? {
                        pin: pinCode
                    }
                    : null,
            knx_settings: {
                address: knxSetAddress
            }
        }
        console.log(input)
        return rpc
            .updateInput(input)
            .then(() => {
                const message =
                    selected?.id != null
                        ? t("input_successfully_edited")
                        : previousLocationSelectedControl?.id == null
                            ? t("input_successfully_saved")
                            : t("control_and_input_successfully_saved")
                success(message)
                onBackPressed()
            })
            .catch(failure)
    }, [
        t,
        isFieldsValid,
        rpc,
        success,
        failure,
        selected,
        previousLocationSelectedControl?.id,
        onBackPressed,
        name,
        bridgeDevice,
        inputType,
        direction,
        outputReverse,
        inputNumber,
        controlOption,
        sensorTypeOption,
        remoteInput,
        pinCode,
        bridgeDeviceType,
        knxSetAddress,
        hysteresis,
        analogSensorTypeOption
    ])

    const buttons = useMemo(() => [
        {
            text: t('delete'),
            className: 'mulberry-wood',
            onClick: onDeletePressed,
            hidden: !(selected?.id),
        },
        {
            text: t('save'),
            className: 'green',
            onClick: onCreatePressed,
        },
        {
            text: t('cancel'),
            className: 'red',
            onClick: onGoBackPressed,
        },
    ], [
        t,
        onCreatePressed,
        onDeletePressed,
        selected,
        onGoBackPressed,
    ]);

    const onBlur = useCallback(() => {
        setActiveNumpadInput(null)
        setActiveInput(null)
    }, [setActiveInput, setActiveNumpadInput])

    const setOutputReverseCheckbox = useCallback(() => {
        setOutputReverse(!outputReverse);
    }, [outputReverse]);

    useEffect(() => {
        setControlOption(controlOptionList.find(controlOptionItem => controlOptionItem.value.id === controlOption?.value.id) || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlOptionList]);

    useEffect(() => {
        setInputNumber(inputNumberOptionList.find(option => option.value === inputNumber?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputNumberOptionList]);

    useEffect(() => console.log(inputType), [inputType])

    useEffect(() => {
        if (previousLocationSelectedControl?.id != null) {
            setControlOption(controlOptionList.find(controlOption =>
                controlOption.value.id === previousLocationSelectedControl.id) || null);

            const nameSuffix = previousLocationSelectedControl.type_id === CONTROL_TYPE_ID_ALARM ?
                t('its_sensor') :
                t('its_key');
            setName(previousLocationSelectedControl.name + nameSuffix);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t, previousLocationSelectedControl?.id]);

    return (
        <PasswordContainer requiredRole="service" isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className="service-edit"
                backUrl={BACK_URL}
                currentInput={currentInput}
            >
                <div className="content-divider">
                    <div className="content-wrapper">
                        {renderIf(inputType?.value?.id === INPUT_TYPE_ID_ADV_TEMPERATURE && selected?.id != null)(
                            () => (
                                <SettingReadOnlyRowComponent title={t("input_name")} value={name} />
                            ),
                            () => (
                                <SettingInputComponent
                                    placeholder={t("write_input_name")}
                                    label={t("input_name")}
                                    value={name}
                                    setValue={setName}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                            )
                        )}
                        {renderIf(inputType?.value?.id === INPUT_TYPE_ID_ADV_TEMPERATURE && selected?.id != null)(
                            () => (
                                <SettingReadOnlyRowComponent title={t("device")} value={bridgeDevice?.label} />
                            ),
                            () => (
                                <SettingSelectComponent
                                    label={t("device")}
                                    options={bridgeDeviceOptionList}
                                    onChange={setBridgeDevice}
                                    value={bridgeDevice}
                                />
                            )
                        )}
                        {renderIf(inputType?.value?.id === INPUT_TYPE_ID_ADV_TEMPERATURE && selected?.id != null)(
                            () => (
                                <SettingReadOnlyRowComponent title={t("input_type")} value={inputType?.label} />
                            ),
                            () => (
                                <SettingSelectComponent
                                    label={t("input_type")}
                                    options={inputTypeOptionList}
                                    onChange={setInputType}
                                    value={inputType} />
                            ))
                        }
                        {
                            renderIf(bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_KNX)(() => (
                                <SettingInputComponent
                                    placeholder={t("set_address")}
                                    label={t("set_address")}
                                    value={knxSetAddress}
                                    setValue={setKnxSetAddress}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur} />
                            ))
                        }
                        {
                            renderIf(bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KEYPAD && bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KNX)(() => (
                                <SettingCheckBoxComponent
                                    label={t("remote_input")}
                                    set={() => setRemoteInput(!remoteInput)}
                                    value={remoteInput} />
                            ))
                        }
                        {
                            renderIf(inputType?.value?.id === INPUT_TYPE_ID_SENSOR)(() =>
                                <SettingSelectComponent
                                    label={t('sensor_type')}
                                    options={sensorTypeOptionList}
                                    onChange={setSensorTypeOption}
                                    value={sensorTypeOption} />)
                        }
                        {
                            renderIf(inputType?.value != null)(() =>
                                <>
                                    {
                                        renderIf(inputType.value.id !== INPUT_TYPE_ID_SCENARIO)(() =>
                                            <>
                                                <SettingSelectComponent
                                                    label={t('control')}
                                                    options={controlOptionList}
                                                    onChange={setControlOption}
                                                    value={controlOption} />
                                                {
                                                    renderIf(inputType?.value?.id === INPUT_TYPE_ID_ANALOG)(() => (
                                                        <>
                                                            <SettingSelectComponent
                                                                label={t('analog_sensor_type')}
                                                                options={analogSensorTypeOptionList}
                                                                onChange={setAnalogSensorTypeOption}
                                                                value={analogSensorTypeOption} />
                                                            <SettingInputComponent
                                                                label={t('temperature_hysteresis')}
                                                                value={hysteresis.toString()}
                                                                // TODO: Find better solution.
                                                                setValue={(newValue: string) => setHysteresis(Number(newValue))}
                                                                unit={'x10 mV'}
                                                                setActiveInput={setActiveInput}
                                                                onBlur={onBlur} />
                                                        </>
                                                    ))
                                                }
                                                {
                                                    renderIf((bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KEYPAD && inputType.value.id === INPUT_TYPE_ID_SCENARIO) || (inputType.value.id !== INPUT_TYPE_ID_SENSOR && inputType?.value?.id !== INPUT_TYPE_ID_ADV_TEMPERATURE))(() =>
                                                        <SettingSelectComponent
                                                            label={t('direction')}
                                                            options={DIRECTION_OPTION_LIST}
                                                            onChange={setDirection}
                                                            value={direction} />,
                                                    )
                                                }
                                            </>,
                                        )
                                    }
                                    {
                                        renderIf(bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_KEYPAD)(() => (
                                            <SettingInputComponent
                                                placeholder={t('write_pin_code')}
                                                label={t('pin_code')}
                                                value={pinCode}
                                                maxLength={4}
                                                setValue={setPinCode}
                                                setActiveInput={setActiveNumpadInput}
                                                onBlur={onBlur} />
                                        ))
                                    }
                                    {
                                        renderIf(
                                            inputType?.value?.id !== INPUT_TYPE_ID_ANALOG &&
                                            inputType?.value?.id !== INPUT_TYPE_ID_ADV_TEMPERATURE &&
                                            bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KEYPAD &&
                                            bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KNX
                                        )(() => (
                                            <SettingSelectComponent
                                                label={t('input_no')}
                                                options={inputNumberOptionList}
                                                onChange={setInputNumber}
                                                value={inputNumber} />
                                        ))
                                    }
                                </>,
                            )}
                        {renderIf(inputType?.value?.id === INPUT_TYPE_ID_ADV_TEMPERATURE && selected?.id != null)(
                            () => (
                                <SettingReadOnlyRowComponent
                                    title={t("sensor_number")}
                                    value={selected?.input_number.toString()}
                                />
                            )
                        )}
                    </div>
                    <div className='content-wrapper'>
                        {renderIf(inputType?.value?.id !== INPUT_TYPE_ID_ADV_TEMPERATURE && bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KEYPAD && bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KNX)(() => (
                            <SettingCheckBoxComponent
                                label={t("reverse")}
                                set={setOutputReverseCheckbox}
                                //todo , .
                                value={outputReverse}
                            />
                        ))}
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
            {
                renderNumpadKeyboard()
            }
        </PasswordContainer>
    )
});
